<template>
    <li class="v-atlas-raumplanung-toc-filter-tree-node">

        <div class="v-atlas-raumplanung-toc-filter-tree-node__row" :class="{
            'v-atlas-raumplanung-toc-filter-tree-node__row--parent': hasChildren,
            'v-atlas-raumplanung-toc-filter-tree-node__row--expanded': expanded,
            'v-atlas-raumplanung-toc-filter-tree-node__row--selected': isSelected,
            'v-atlas-raumplanung-toc-filter-tree-node__row--pinned': isPinned,
            'v-atlas-raumplanung-toc-filter-tree-node__row--active-path': isOnActivePath,
            'v-atlas-raumplanung-toc-filter-tree-node__row--collapsed': hasChildren && !expanded,
        }">
            <button v-if="!isTextNode" class="v-atlas-raumplanung-toc-filter-tree-node__select"
                :data-tooltip="hasChildren ? 'Kategorie auf-/zuklappen' : 'Nach dieser Kategorie filtern'"
                @click="onRowClick">
                <span class="v-atlas-raumplanung-toc-filter-tree-node__label"
                    :class="{ 'v-atlas-raumplanung-toc-filter-tree-node__label--parent': hasChildren }">
                    {{ node.label }}
                </span>
            </button>

            <!-- Pin icon button commented out: the label button now toggles pin/unpin.
            <button v-if="!hasChildren && pinningEnabled" class="v-atlas-raumplanung-toc-filter-tree-node__pin"
                :class="{ 'v-atlas-raumplanung-toc-filter-tree-node__pin--active': isPinned }"
                :data-tooltip="isPinned ? 'Filter lösen' : 'Filter anpinnen'" @click.stop="onPinClick">
                {{ isPinned ? '📍' : '📌' }}
            </button>
            -->
        </div>

        <ul v-if="expanded && hasChildren" class="v-atlas-raumplanung-toc-filter-tree-node__children">
            <li v-for="child in childrenView" :key="child.node.tag">
                <TocFilterTreeNode :node="child.node" :selected="child.selected" :active-path="child.activePath"
                    :pinned="child.pinned" :allow-pinning="child.allowPinning" :children-view="child.children"
                    :text-query="textQuery" :is-text-node="child.isTextNode" @select="emitSelect" @pin="emitPin"
                    @unpin="emitUnpin" @update:text="emitText" />
            </li>
        </ul>

        <!-- Text search input for the synthetic text node -->
        <div v-if="isTextNode" class="v-atlas-raumplanung-toc-filter-tree-node__text">
            <SmartTextInput :model-value="textQuery" placeholder="Nach Indikatortitel filtern"
                @update:model-value="onTextInput" />
        </div>
    </li>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { RaumplanungTocFilterTreeNode } from 'atlas-shared';
import { SmartTextInput } from 'gertrud';

interface DecoratedTreeNode {
    node: RaumplanungTocFilterTreeNode;
    filterId: string | null;
    selected: boolean;
    activePath: boolean;
    pinned: boolean;
    allowPinning: boolean;
    isTextNode: boolean;
    children: DecoratedTreeNode[];
}

const props = withDefaults(defineProps<{
    node: RaumplanungTocFilterTreeNode;
    selected: boolean;
    activePath: boolean;
    pinned: boolean;
    allowPinning?: boolean;
    childrenView: DecoratedTreeNode[];
    textQuery?: string;
    isTextNode?: boolean;
}>(), {
    allowPinning: true,
});

const emit = defineEmits<{
    select: [tag: string | null];
    pin: [tag: string];
    unpin: [tag: string];
    'update:text': [value: string];
}>();

// Start with the tree collapsed: nodes only expand when the row is clicked.
const expanded = ref(false);

const hasChildren = computed(() => (props.node.children?.length ?? 0) > 0);

/**
 * Whether pinning is enabled for this node. Defaults to `true`; the parent tree
 * passes `false` when this node or any ancestor has `allowPinning` set, which
 * hides the pin button for all filter nodes in the sub-branch.
 */
const pinningEnabled = computed(() => props.allowPinning);

/**
 * Presentation state is computed by the parent tree from the associated
 * filter's actual activity in the registry (decoupled from any tag-string
 * semantics) and passed down via props. Each node just reflects the booleans
 * it receives and propagates them (plus `childrenView`) to its subtree.
 */
const isSelected = computed(() => props.selected);
const isPinned = computed(() => props.pinned);
const isOnActivePath = computed(() => props.activePath);

function onRowClick() {
    // For category nodes (with children), clicking the row toggles expand/collapse.
    if (hasChildren.value) {
        expanded.value = !expanded.value
        return
    }
    // The text node's pin state is driven entirely by the content of its input
    // field (see TocFilterTree's textQuery setter); clicking its label has no
    // effect.
    if (props.isTextNode) {
        return
    }
    // For other leaf nodes, the label button behaves like the pin button: clicking it
    // pins the filter, clicking it again unpins it.
    onPinClick()
}

function onPinClick() {
    // Only leaf nodes have a pin button.
    if (hasChildren.value) return
    if (isPinned.value) {
        emitUnpin(props.node.tag)
    } else {
        emitPin(props.node.tag)
    }
}

function emitSelect(tag: string | null) {
    emit('select', tag);
}

function emitPin(tag: string) {
    emit('pin', tag);
}

function emitUnpin(tag: string) {
    emit('unpin', tag);
}

function emitText(value: string) {
    emit('update:text', value);
}

function onTextInput(value: string) {
    emit('update:text', value);
}
</script>